import { useEffect, useState } from 'react';
import {
  ACTIONS,
  clashOf,
  isDefault,
  keyLabel,
  keysOf,
  refusal,
  removeKey,
  resetAction,
  resetAll,
  setCapturing,
  setKey,
  strokeOf,
  useShortcutKeys,
  type KeyStroke,
  type ShortcutAction,
} from '../shortcuts.ts';

/**
 * Settings > Keyboard shortcuts, as the desktop's: every action with its keys. Click a key
 * and press new ones (Esc cancels), + Add key for another, × to remove one, Default to put
 * an action's own keys back. A clash offers Swap, Replace or Cancel; keys that can't be
 * shortcuts are refused with the reason.
 */
export function ShortcutSettings({ onUnsaved }: { onUnsaved: () => void }) {
  useShortcutKeys();
  const [editing, setEditing] = useState<{ actionId: string; replacing: KeyStroke | null } | null>(null);
  const [problem, setProblem] = useState('');
  const [clash, setClash] = useState<{ actionId: string; stroke: KeyStroke; replacing: KeyStroke | null; other: ShortcutAction } | null>(null);

  useEffect(() => {
    if (!editing) return;
    setCapturing(true);
    const onKey = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Escape') {
        setEditing(null);
        return;
      }
      const stroke = strokeOf(event);
      if (!stroke) return; // a modifier on its own: wait for the key
      const refused = refusal(stroke);
      if (refused) {
        setProblem(refused);
        return;
      }
      const other = clashOf(editing.actionId, stroke);
      setEditing(null);
      if (other) {
        setClash({ actionId: editing.actionId, stroke, replacing: editing.replacing, other });
        return;
      }
      if (!setKey(editing.actionId, stroke, editing.replacing)) onUnsaved();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      setCapturing(false);
    };
  }, [editing, onUnsaved]);

  const groups = [...new Set(ACTIONS.map((a) => a.group))];
  return (
    <>
      <p className="meta">
        Click a key, then press the new keys; Esc cancels. Keys that would be typed, keys that keep their usual meaning, and
        keys the browser keeps for itself can't be used.
      </p>
      {clash && (
        <div className="panel confirm" role="alertdialog" aria-label="That key is taken">
          <p>
            {keyLabel(clash.stroke)} already belongs to “{clash.other.name}”.
          </p>
          <div className="actions">
            {clash.replacing && (
              <button
                type="button"
                onClick={() => {
                  if (!setKey(clash.actionId, clash.stroke, clash.replacing, 'swap')) onUnsaved();
                  setClash(null);
                }}
              >
                Swap
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (!setKey(clash.actionId, clash.stroke, clash.replacing, 'replace')) onUnsaved();
                setClash(null);
              }}
            >
              Replace
            </button>
            <button type="button" className="quiet" onClick={() => setClash(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {problem && (
        <p className="notice notice-error" role="alert">
          {problem}
        </p>
      )}
      {groups.map((group) => (
        <table key={group} className="shortcut-table">
          <caption>{group}</caption>
          <tbody>
            {ACTIONS.filter((a) => a.group === group).map((action) => (
              <tr key={action.id}>
                <th scope="row">{action.name}</th>
                <td>
                  {keysOf(action.id).map((stroke) => (
                    <span key={keyLabel(stroke)} className="shortcut-key">
                      <button
                        type="button"
                        className="key-button"
                        aria-label={`Change ${keyLabel(stroke)} for ${action.name}`}
                        onClick={() => {
                          setProblem('');
                          setEditing({ actionId: action.id, replacing: stroke });
                        }}
                      >
                        <kbd>{keyLabel(stroke)}</kbd>
                      </button>
                      <button type="button" className="quiet" aria-label={`Remove ${keyLabel(stroke)} from ${action.name}`} onClick={() => removeKey(action.id, stroke) || onUnsaved()}>
                        ×
                      </button>
                    </span>
                  ))}
                  {editing?.actionId === action.id ? (
                    <span className="shortcut-capture" role="status">
                      Press the new keys…
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="quiet"
                      onClick={() => {
                        setProblem('');
                        setEditing({ actionId: action.id, replacing: null });
                      }}
                    >
                      + Add key
                    </button>
                  )}
                  {!isDefault(action.id) && (
                    <button type="button" className="quiet" onClick={() => resetAction(action.id) || onUnsaved()}>
                      Default
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
      <div className="actions">
        <button type="button" onClick={() => resetAll() || onUnsaved()}>
          Use Realmery's keys
        </button>
      </div>
    </>
  );
}
